import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import {
    Button,
    Chip,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    withStyles,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CallIcon from '@material-ui/icons/Call';
import CallDetailsDrawer from './CallDetailsDrawer';
import HttpService from '../../services/httpService';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';

// What the ERP did with each call's answer (CallingDeskResult.action).
const ACTIONS = [
    { key: '', label: 'All' },
    { key: 'rnc_ready', label: 'RNC collected - ready to take action', color: '#1565c0' },
    { key: 'design_rnr_response', label: 'Design RNR - response received', color: '#6a1b9a' },
    { key: 'design_hold_response', label: 'Design-Hold - response received', color: '#00838f' },
    { key: 'quotation_followup_response', label: 'Quotation follow-up - response received', color: '#ad1457' },
    { key: 'lost_deals_response', label: 'Lost Deals - response received', color: '#4e342e' },
    { key: 'mql_mnql_result', label: 'MQL / MNQL - call recorded', color: '#0277bd' },
    { key: 'm2m_rnr_result', label: 'M2M RNR - call recorded', color: '#558b2f' },
    { key: 'moved_to_lead_received', label: 'Moved to Lead Received', color: '#2e7d32' },
    { key: 'moved_to_junk', label: 'Moved to Junk', color: '#6d4c41' },
    { key: 'conflict', label: 'Conflict', color: '#ef6c00' },
    { key: 'needs_attention', label: 'Needs attention', color: '#c62828' },
    { key: 'no_change', label: 'No change', color: '#546e7a' },
    { key: 'unmatched', label: 'Unmatched', color: '#78909c' },
];

const EMPTY_FILTERS = { callType: '', startDate: '', endDate: '', search: '' };

// What the agents collected (aiResult keys - RNC, Design RNR, Design-Hold,
// Quotation follow-up, MQL / MNQL), shown under the summary.
const COLLECTED_FIELDS = [
    ['city', 'City'],
    ['scope', 'Scope'],
    ['timeline', 'Timeline'],
    ['visit_date', 'Visit date'],
    ['visit_time', 'Visit time'],
    ['nextcall', 'Call back'],
    ['property_name', 'Property'],
    ['property_area', 'Locality'],
    ['bhk', 'BHK'],
    ['scope_of_work', 'Scope'],
    ['budget', 'Budget'],
    ['floor_plan_available', 'Floor plan'],
    ['quotation_received', 'Quotation received'],
    ['quotation_seen', 'Quotation seen'],
    ['quotation_feedback', 'Feedback'],
    ['discussion_type', 'Discussion'],
    ['discussion_points', 'To cover'],
    ['ec', 'EC'],
    ['preferred_date', 'Date'],
    ['preferred_time', 'Time'],
    ['callback_date', 'Callback date'],
    ['callback_time', 'Callback time'],
    ['special_requirements', 'Notes'],
    ['customer_decision', 'Decision'],
    ['team_connect_date', 'Connect on'],
    ['team_connect_time', 'Connect at'],
    ['hold_reason', 'Hold reason'],
    ['hold_reason_confirmed', 'Hold reason'],
    ['hold_resolved', 'Hold resolved'],
    ['possession_received', 'Possession received'],
    ['ready_to_pay_50', 'Ready to pay 50%'],
    ['payment_ready_by', 'Payment by'],
    ['design_restart_date', 'Restart on'],
    ['hold_till_date', 'Hold till'],
    ['customer_concerns', 'Concerns'],
    ['unavailability_reason', 'Unreachable because'],
    ['updated_whatsapp', 'New WhatsApp'],
    ['updated_email', 'New email'],
];

// The Lost Deals agent's answers aren't fixed yet, so all of them are shown.
const NOT_AN_ANSWER = ['summary', 'outcome', 'call_outcome'];
const labelOf = (key) => key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
const collectedOf = (row) => (row.callType === 'lost_deals'
    ? Object.keys(row.aiResult || {}).filter((key) => !NOT_AN_ANSWER.includes(key)).map((key) => [key, labelOf(key)])
    : COLLECTED_FIELDS
).filter(([key]) => row.aiResult?.[key] && typeof row.aiResult[key] !== 'object');

const StyledTableCell = withStyles(() => ({
    head: { backgroundColor: '#1C375D', color: '#FFF2EB', fontWeight: 500, whiteSpace: 'nowrap', padding: '12px 14px' },
    body: { fontSize: 13, verticalAlign: 'top', padding: '12px 14px', borderBottom: '1px solid #e8edf2', color: '#263238' },
}))(TableCell);

const useStyles = makeStyles(() => ({
    paper: { padding: 16, marginTop: 16 },
    title: { color: '#1C375D', margin: '4px 0 12px', fontSize: 20, fontWeight: 600 },
    tabs: { display: 'flex', flexWrap: 'wrap', gap: 8, margin: '16px 0 4px' },
    submitButton: { backgroundColor: '#F47E46', color: '#FFF2EB', '&:hover': { backgroundColor: '#e06d36' } },
    muted: { color: '#78909c', fontSize: 12 },
    statusArrow: { color: '#78909c', margin: '0 4px' },
    row: {
        '&:nth-of-type(even)': { backgroundColor: '#f8fafc' },
        '&:hover': { backgroundColor: '#eef4fa' },
    },
    customer: { fontWeight: 600, color: '#1C375D', marginBottom: 2 },
    leadLink: { color: '#1C375D', textDecoration: 'underline' },
    callType: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, backgroundColor: '#eef1f4', color: '#37474f', fontSize: 12, fontWeight: 600, marginBottom: 6 },
    callState: { display: 'flex', alignItems: 'center', fontSize: 12.5 },
    dot: { width: 8, height: 8, borderRadius: '50%', marginRight: 6, flexShrink: 0 },
    callButton: { marginTop: 6, backgroundColor: '#FFF1EA', color: '#F47E46', '&:hover': { backgroundColor: '#FFE0D1' } },
    chip: { height: 'auto', color: '#fff', fontWeight: 500, maxWidth: '100%' },
    chipLabel: { whiteSpace: 'normal', padding: '4px 10px', lineHeight: 1.3 },
    verdict: { fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: '#455a64', marginTop: 8, textTransform: 'uppercase' },
    note: { color: '#546e7a', fontSize: 12, lineHeight: 1.45, marginTop: 4 },
    statusLine: { fontSize: 12, marginTop: 8, color: '#37474f' },
    summaryText: { fontSize: 13.5, lineHeight: 1.55, color: '#263238' },
    clamped: { display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
    moreToggle: { background: 'none', border: 0, padding: 0, marginTop: 4, color: '#F47E46', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    answers: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
    answer: { padding: '3px 9px', borderRadius: 12, backgroundColor: '#eef3f8', color: '#1C375D', fontSize: 12, lineHeight: 1.45, wordBreak: 'break-word' },
    answerLabel: { color: '#5f7a95', fontWeight: 600, marginRight: 4 },
}));

const formatStatus = (status) => (status ? [status.leadType, status.status].filter(Boolean).join(' / ') : '');
const formatDuration = (seconds) => {
    const total = Math.round(seconds);
    return total >= 60 ? `${Math.floor(total / 60)}m ${total % 60}s` : `${total}s`;
};
// A summary longer than this starts folded to 4 lines, with "Show more".
const LONG_SUMMARY = 220;

const CallingDeskResults = (props) => {
    const classes = useStyles();
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
    const [action, setAction] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [data, setData] = useState({ results: [], total: 0, counts: {} });
    const [callTypes, setCallTypes] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});
    const [callRow, setCallRow] = useState(null); // the row whose recording/transcript panel is open

    const toggleRow = (id) => setExpandedRows((current) => ({ ...current, [id]: !current[id] }));

    useEffect(() => {
        const fetchResults = async () => {
            props.setLoading(true);
            try {
                const response = await HttpService.getCallingDeskResults({
                    ...appliedFilters,
                    action,
                    page: page + 1,
                    limit: rowsPerPage,
                });
                setData({
                    results: response?.data?.results || [],
                    total: response?.data?.total || 0,
                    counts: response?.data?.counts || {},
                });
                setCallTypes(response?.data?.callTypes || []);
            } catch (error) {
                props.setSnackInfo(error?.response?.data?.message || 'Could not load call results', 'error');
            }
            props.setLoading(false);
        };
        fetchResults();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters, action, page, rowsPerPage]);

    const updateFilter = (key) => (event) => setFilters({ ...filters, [key]: event.target.value });

    const applyFilters = () => {
        setPage(0);
        setAppliedFilters({ ...filters });
    };

    const clearFilters = () => {
        setPage(0);
        setFilters(EMPTY_FILTERS);
        setAppliedFilters(EMPTY_FILTERS);
    };

    const totalForTab = (key) => (key
        ? data.counts[key] || 0
        : Object.values(data.counts).reduce((sum, count) => sum + count, 0));

    return (
        <div>
            <Paper className={classes.paper}>
                <div className={classes.title}>Calling Desk - Results</div>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <TextField select fullWidth variant="outlined" size="small" label="Call type" value={filters.callType} onChange={updateFilter('callType')}>
                            <MenuItem value="">All call types</MenuItem>
                            {callTypes.map((type) => <MenuItem key={type.key} value={type.key}>{type.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField fullWidth variant="outlined" size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.startDate} onChange={updateFilter('startDate')} />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField fullWidth variant="outlined" size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.endDate} onChange={updateFilter('endDate')} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            label="Lead no. / customer"
                            value={filters.search}
                            onChange={updateFilter('search')}
                            onKeyDown={(event) => { if (event.key === 'Enter') applyFilters(); }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2} style={{ display: 'flex', gap: 8 }}>
                        <Button variant="contained" className={classes.submitButton} onClick={applyFilters}>Submit</Button>
                        <Button variant="outlined" onClick={clearFilters}>Clear</Button>
                    </Grid>
                </Grid>

                <div className={classes.tabs}>
                    {ACTIONS.map((item) => (
                        <Chip
                            key={item.key || 'all'}
                            clickable
                            label={`${item.label} (${totalForTab(item.key)})`}
                            onClick={() => { setPage(0); setAction(item.key); }}
                            style={action === item.key
                                ? { backgroundColor: item.color || '#1C375D', color: '#fff' }
                                : { backgroundColor: '#fff', color: item.color || '#1C375D', border: `1px solid ${item.color || '#1C375D'}` }}
                        />
                    ))}
                </div>
            </Paper>

            <TableContainer component={Paper} style={{ marginTop: 16, maxHeight: 'calc(100vh - 290px)', minHeight: 320 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>When</StyledTableCell>
                            <StyledTableCell>Lead</StyledTableCell>
                            <StyledTableCell>Call</StyledTableCell>
                            <StyledTableCell>Result</StyledTableCell>
                            <StyledTableCell>Summary &amp; answers</StyledTableCell>
                            <StyledTableCell>Requested by</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.results.length === 0 && (
                            <TableRow>
                                <StyledTableCell colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#78909c' }}>No call results yet</StyledTableCell>
                            </TableRow>
                        )}
                        {data.results.map((row) => {
                            const actionInfo = ACTIONS.find((item) => item.key === row.action) || {};
                            const verdict = row.aiResult?.design ?? row.aiResult?.ready_to_start ?? row.aiResult?.final_verdict
                                ?? row.aiResult?.call_outcome ?? row.aiResult?.customer_decision;
                            const collected = collectedOf(row);
                            const before = formatStatus(row.statusBefore);
                            const after = formatStatus(row.statusAfter);
                            const summary = typeof row.aiResult?.summary === 'string' ? row.aiResult.summary : '';
                            const isLong = summary.length > LONG_SUMMARY;
                            const expanded = Boolean(expandedRows[row._id]);
                            const connected = row.call?.connected;
                            return (
                                <TableRow key={row._id} className={classes.row}>
                                    <StyledTableCell style={{ whiteSpace: 'nowrap' }}>
                                        <div>{moment(row.createdAt).format('DD MMM YYYY')}</div>
                                        <div className={classes.muted}>{moment(row.createdAt).format('hh:mm A')}</div>
                                    </StyledTableCell>
                                    <StyledTableCell style={{ minWidth: 150 }}>
                                        <div className={classes.customer}>{row.customerName || '-'}</div>
                                        {row.leadId
                                            ? <Link to={`/project-logs/${row.leadId}`} target="_blank" rel="noopener noreferrer" className={classes.leadLink}>{row.leadNo || 'Open lead'}</Link>
                                            : <span className={classes.muted}>No lead matched</span>}
                                        {row.uniqueLeadNumber && <div className={classes.muted}>{row.uniqueLeadNumber}</div>}
                                    </StyledTableCell>
                                    <StyledTableCell style={{ minWidth: 150 }}>
                                        <div className={classes.callType}>{row.callTypeLabel || row.callType}</div>
                                        <div className={classes.callState}>
                                            <span className={classes.dot} style={{ backgroundColor: connected === true ? '#2e7d32' : connected === false ? '#c62828' : '#b0bec5' }} />
                                            {connected === true ? 'Connected' : connected === false ? 'Not connected' : (row.call?.outcome || 'Unknown')}
                                        </div>
                                        <div className={classes.muted}>
                                            {[row.call?.duration ? formatDuration(row.call.duration) : '', row.attempt ? `Attempt ${row.attempt}` : ''].filter(Boolean).join(' · ')}
                                        </div>
                                        {(row.call?.recording || row.conversationId) && (
                                            <div>
                                                <Tooltip title="Recording & transcript">
                                                    <IconButton size="small" className={classes.callButton} onClick={() => setCallRow(row)} aria-label="Open recording and transcript">
                                                        <CallIcon style={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell style={{ minWidth: 220, maxWidth: 300 }}>
                                        <Chip
                                            size="small"
                                            label={actionInfo.label || row.action}
                                            className={classes.chip}
                                            classes={{ label: classes.chipLabel }}
                                            style={{ backgroundColor: actionInfo.color || '#78909c' }}
                                        />
                                        {verdict && <div className={classes.verdict}>{String(verdict).replace(/_/g, ' ')}</div>}
                                        {row.note && <div className={classes.note}>{row.note}</div>}
                                        {after && (
                                            <div className={classes.statusLine}>
                                                {before}<span className={classes.statusArrow}>&rarr;</span><b>{after}</b>
                                                {row.statusAfter?.assignToName && <div className={classes.muted}>Assigned to {row.statusAfter.assignToName}</div>}
                                            </div>
                                        )}
                                        {!after && before && <div className={classes.statusLine}><span className={classes.muted}>Lead: </span>{before}</div>}
                                    </StyledTableCell>
                                    <StyledTableCell style={{ minWidth: 380 }}>
                                        {summary
                                            ? <div className={`${classes.summaryText} ${isLong && !expanded ? classes.clamped : ''}`}>{summary}</div>
                                            : <span className={classes.muted}>No summary</span>}
                                        {isLong && (
                                            <button type="button" className={classes.moreToggle} onClick={() => toggleRow(row._id)}>
                                                {expanded ? 'Show less' : 'Show more'}
                                            </button>
                                        )}
                                        {collected.length > 0 && (
                                            <div className={classes.answers}>
                                                {collected.map(([key, label]) => (
                                                    <span key={key} className={classes.answer}>
                                                        <span className={classes.answerLabel}>{label}</span>{String(row.aiResult[key])}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell style={{ whiteSpace: 'nowrap' }}>{row.requestedByName || <span className={classes.muted}>-</span>}</StyledTableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={data.total}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
            />
            <CallDetailsDrawer row={callRow} onClose={() => setCallRow(null)} />
        </div>
    );
};

export default connect(null, { setLoading, setSnackInfo })(CallingDeskResults);
